import React from 'react';
import { Icon } from '@tviklax/ui-components';
import { SchedulingContextHeaderProps, HealthStatus } from './SchedulingContextTypes';
import {
  HeaderContainer,
  HeaderContent,
  TopRow,
  OrderIdSection,
  OrderId,
  ContextSection,
  ContextLabel,
  ContextValue,
  StageBadge,
  StageLabel,
  StageIndex,
  HealthBadge,
  HealthBadgeContent,
  HealthDot,
  HealthText,
  MetaDataRow,
  MetaItem,
  MetaLabel,
  MetaValue,
  NextActionBox,
  ActionIcon,
  ActionText,
} from './SchedulingContextStyles';

const getHealthColor = (status: HealthStatus): string => {
  switch (status) {
    case 'on-track':
      return '#10b981';
    case 'at-risk':
      return '#f59e0b';
    case 'delayed':
      return '#ef4444';
    default:
      return '#6b7280';
  }
};

const getHealthLabel = (status: HealthStatus): string => {
  switch (status) {
    case 'on-track':
      return 'On Track';
    case 'at-risk':
      return 'At Risk';
    case 'delayed':
      return 'Delayed';
    default:
      return 'Unknown';
  }
};

export const SchedulingContextHeader: React.FC<SchedulingContextHeaderProps> = ({
  orderId,
  customerName,
  plant,
  currentStage,
  stageIndex,
  totalStages,
  healthStatus,
  promisedDate,
  lastUpdate,
  nextSystemAction,
}) => {
  const healthColor = getHealthColor(healthStatus);
  const healthLabel = getHealthLabel(healthStatus);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <HeaderContainer>
      <HeaderContent>
        <TopRow>
          <OrderIdSection>
            <OrderId>{orderId}</OrderId>
          </OrderIdSection>

          <ContextSection>
            <div>
              <ContextLabel>Customer</ContextLabel>
              <ContextValue>{customerName}</ContextValue>
            </div>
            <div>
              <ContextLabel>Plant</ContextLabel>
              <ContextValue>{plant}</ContextValue>
            </div>
          </ContextSection>

          <StageBadge>
            <StageLabel>{currentStage}</StageLabel>
            <StageIndex>
              {stageIndex}/{totalStages}
            </StageIndex>
          </StageBadge>

          <HealthBadge $healthColor={healthColor}>
            <HealthBadgeContent>
              <HealthDot $color={healthColor} />
              <HealthText>{healthLabel}</HealthText>
            </HealthBadgeContent>
          </HealthBadge>
        </TopRow>

        <MetaDataRow>
          <MetaItem>
            <MetaLabel>Promised Date</MetaLabel>
            <MetaValue>{formatDate(promisedDate)}</MetaValue>
          </MetaItem>

          <MetaItem>
            <MetaLabel>Last Update</MetaLabel>
            <MetaValue>{formatTime(lastUpdate)}</MetaValue>
          </MetaItem>

          <NextActionBox>
            <ActionIcon>
              <Icon name="Clock" size={16} />
            </ActionIcon>
            <ActionText>
              <MetaLabel>Next Action</MetaLabel>
              <MetaValue>{nextSystemAction}</MetaValue>
            </ActionText>
          </NextActionBox>
        </MetaDataRow>
      </HeaderContent>
    </HeaderContainer>
  );
};
